import React from 'react';

/**
 * Editorial top of the Board Editor: brand + title + client name, action
 * buttons (settings / lock-unlock / back), the collapsible Client Access
 * meta strip, and the collapsible Custom Client Message block.
 *
 * Pure presentational. Owns no state — every value comes from props so the
 * editor itself remains the single source of truth.
 */
const BoardTopbar = ({
  t,
  navigate,
  board,
  brandName,
  workspaceLang,
  showSettings,
  setShowSettings,
  onLockBoard,
  onUnlockBoard,

  // Client Access strip
  isClientLinkOpen,
  setIsClientLinkOpen,
  showShareLink,
  setShowShareLink,
  shareLinkInputRef,
  linkCopied,
  copyShareLink,
  handleRevealShareLink,
  setShowExtendModal,
  isExpired,
  featureAccess,
  showToast,
  boardsAPI,
}) => {
  return (
    <>
      <div className="board-top" data-testid="board-top">
        <div className="board-top__header">
          <div style={{ minWidth: 0, flex: '1 1 auto' }}>
            <p className="board-top__brand-label" data-testid="board-top-brand">
              {brandName || t.studio_name || 'Maestra'}
            </p>
            <h1 className="board-top__title" data-testid="board-top-title">
              {board.name}
              <span
                className={`board-top__title-status status-${board.current_status?.toLowerCase()}`}
                style={{
                  color: board.current_status === 'OPEN' ? '#5C7A56'
                       : board.current_status === 'LOCKED' ? '#7E6A3A'
                       : '#A04141',
                  borderColor: board.current_status === 'OPEN' ? 'rgba(122,155,114,0.45)'
                       : board.current_status === 'LOCKED' ? 'rgba(166,140,75,0.45)'
                       : 'rgba(194,106,106,0.45)',
                  background: board.current_status === 'OPEN' ? 'rgba(122,155,114,0.06)'
                       : board.current_status === 'LOCKED' ? 'rgba(166,140,75,0.06)'
                       : 'rgba(194,106,106,0.06)',
                }}
              >
                {board.current_status === 'OPEN' ? t.status_open
                  : board.current_status === 'LOCKED' ? t.status_locked
                  : board.current_status === 'EXPIRED' || board.current_status === 'ARCHIVED' ? t.status_expired
                  : board.current_status}
              </span>
            </h1>
            {board.client_name && (
              <p className="board-top__client" data-testid="board-top-client">
                {board.client_name}
                {board.client_id && (
                  <button
                    type="button"
                    data-testid="board-client-link"
                    onClick={() => navigate(`/clients/${board.client_id}`)}
                    style={{
                      background: 'transparent', border: 'none', padding: 0, marginLeft: 12,
                      cursor: 'pointer', fontFamily: 'Playfair Display, serif', fontSize: 10,
                      letterSpacing: '0.16em', textTransform: 'uppercase', color: '#cf9a3d',
                    }}
                  >{t.cs_open_record || 'Client record'} →</button>
                )}
              </p>
            )}
          </div>

          <div className="board-top__actions">
            <button
              className="btn-ghost"
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/boards')}
            >
              {t.back}
            </button>
            <button
              className="btn-ghost"
              onClick={() => setShowSettings(!showSettings)}
              data-testid="board-settings-btn"
            >
              {t.settings}
            </button>
            {/* Lock / Unlock — hidden on Inspiration Boards.
                Inspiration is read-only by design (no reactions,
                comments, uploads); locking has no semantic meaning. */}
            {board.board_type !== 'inspiration' && (
              board.current_status === 'OPEN' ? (
                <button className="btn-ghost" onClick={onLockBoard} data-testid="lock-board-btn">
                  {t.lock_board}
                </button>
              ) : board.current_status === 'LOCKED' ? (
                <button className="btn-ghost" onClick={onUnlockBoard} data-testid="unlock-board-btn">
                  {t.unlock_board}
                </button>
              ) : null
            )}
          </div>
        </div>

        {/* Quiet inline meta strip — Client Access (collapsible) */}
        <div className="board-top__meta" data-testid="client-link-section">
          <button
            type="button"
            onClick={() => setIsClientLinkOpen((v) => !v)}
            data-testid="client-link-toggle"
            className={`board-top__meta-summary ${isClientLinkOpen ? 'is-open' : ''}`}
            aria-expanded={isClientLinkOpen}
          >
            <span className="board-top__meta-label">{t.client_link}</span>
            <span className="board-top__meta-dot" />
            <span style={{
              color: isExpired ? '#A04141' : 'var(--kahla-text-light)',
              fontStyle: 'italic',
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontSize: '13px',
            }}>
              {isExpired ? t.closed_on : t.available_until}{' '}
              {new Date(board.expires_at).toLocaleDateString(workspaceLang === 'pl' ? 'pl-PL' : workspaceLang === 'de' ? 'de-DE' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <span className="board-top__meta-chevron" aria-hidden="true">›</span>
          </button>

          {isClientLinkOpen && (
            <div className="board-top__meta-actions" data-testid="client-link-actions">
              <button
                type="button"
                onClick={() => setShowExtendModal(true)}
                className="board-top__meta-link"
                data-testid={isExpired ? 'reopen-board-btn' : 'extend-board-btn'}
              >
                {isExpired ? t.reopen_board : t.extend}
              </button>

              <span className="board-top__meta-dot" />

              {!showShareLink ? (
                <button
                  type="button"
                  onClick={handleRevealShareLink}
                  className="board-top__meta-link"
                  data-testid="show-share-link-btn"
                >
                  {t.show_link}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowShareLink(false)}
                  className="board-top__meta-link"
                  data-testid="hide-share-link-btn"
                >
                  {t.hide_link}
                </button>
              )}

              <button
                type="button"
                onClick={copyShareLink}
                className="board-top__meta-link"
                data-testid="copy-share-link-btn"
              >
                {linkCopied ? t.copied : t.copy_link}
              </button>

              <button
                type="button"
                onClick={() => window.open(`/board/${board.share_token}`, '_blank', 'noopener,noreferrer')}
                className="board-top__meta-link"
                data-testid="preview-as-client-btn"
                title={t.preview_as_client}
              >
                {t.preview_as_client}
              </button>

              {board.client_notifications_enabled !== false && featureAccess.allow_client_notifications && (
                board.client_email ? (
                  <button
                    type="button"
                    data-testid="notify-client-btn"
                    title={`Send a fresh-activity email to ${board.client_email}`}
                    className="board-top__meta-link"
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await boardsAPI.notifyClient(board.id);
                        showToast(`Notification sent to ${board.client_email}`);
                      } catch (err) {
                        const detail = err.response?.data?.detail;
                        showToast(detail || 'Failed to send notification');
                      }
                    }}
                  >
                    {t.notify_client}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="notify-client-missing-email-btn"
                    title="Add a client email to enable notifications"
                    className="board-top__meta-link"
                    onClick={() => {
                      setShowSettings(true);
                      setTimeout(() => {
                        const inp = document.querySelector('[data-testid="client-email-input"], input[type="email"]');
                        if (inp) {
                          inp.scrollIntoView({ block: 'center', behavior: 'smooth' });
                          inp.focus();
                        }
                      }, 150);
                    }}
                    style={{ opacity: 0.7 }}
                  >
                    {t.add_client_email_to_notify}
                  </button>
                )
              )}
            </div>
          )}
        </div>

        {isClientLinkOpen && showShareLink && (
          <div className="board-top__share-input">
            <i className="fa-solid fa-link" style={{ fontSize: '11px', color: 'var(--kahla-taupe)' }} />
            <input
              ref={shareLinkInputRef}
              type="text"
              readOnly
              data-testid="share-link-input"
              value={`${window.location.origin}/board/${board.share_token}`}
              onFocus={(e) => e.target.select()}
              onClick={(e) => e.target.select()}
            />
          </div>
        )}

        {/* Note: the collapsible "A note to your client" editor used to
            live here. It was moved out of the topbar so it sits between
            the Shared Client Space divider and the Add Images panel —
            closer to the client-facing context it actually controls. */}
      </div>
    </>
  );
};

export default BoardTopbar;
